import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, Shield, Zap, Clock, CheckCircle } from "lucide-react";

const theme = {
  accent: "#C98225",
  bgFrom: "#07090f",
  bgTo: "#0b0f19",
};

const TELEGRAM_CHAT = "https://t.me/Bytron";

export default function ProductPage() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);

  const PRODUCTS = {
    "Crash": {
      title: "Crash Predictor",
      image: "/1xbet-crash.png",
      desc: "The most stable crash prediction module with high accuracy. Works on 1xBet, 1win, and other platforms.",
      price: 100,
      tutorial: "https://t.me/BYTRON_CHANNEL/795",
      features: ["Real-time analysis", "94% accuracy", "Multi-platform support"],
    },
    "Aviator Spribe": {
      title: "Aviator Spribe Predictor",
      image: "/1win-aviator.png",
      desc: "Advanced algorithm for predicting Aviator rounds with high accuracy. Real-time signals before each round.",
      price: 100,
      tutorial: "https://t.me/BYTRON_CHANNEL/795",
      features: ["AI-powered predictions", "Instant signals", "Works on all casinos"],
    },
    "LuckyJet": {
      title: "LuckyJet Predictor",
      image: "/1win-luckyjet.png",
      desc: "Accurate LuckyJet prediction tool with real-time analysis and instant signals.",
      price: 100,
      tutorial: "https://t.me/BYTRON_CHANNEL/795",
      features: ["Pattern recognition", "Live predictions", "Easy to use"],
    },
    "Mines": {
      title: "Mines Predictor",
      image: "/mines.png",
      desc: "Know exactly where the mines are hidden and win every round. Advanced grid analysis.",
      price: 100,
      tutorial: "https://t.me/BYTRON_CHANNEL/795",
      features: ["Grid analysis", "Safe spot detection", "Configurable difficulty"],
    },
    "Mostbet Aviator Spribe": {
      title: "Mostbet Aviator Predictor",
      image: "/mostbet-aviator.png",
      desc: "Accurate Mostbet Aviator prediction tool with real-time syncing and instant signals.",
      price: 100,
      tutorial: "https://t.me/BYTRON_CHANNEL/795",
      features: ["Mostbet optimized", "Real-time sync", "High accuracy"],
    },
    "Apple of Fortune": {
      title: "Apple of Fortune Predictor",
      image: "/apple-of-fortune.png",
      desc: "Know exactly where the apple is hidden and win every round. Never guess wrong again.",
      price: 100,
      tutorial: "https://t.me/BYTRON_CHANNEL/797",
      features: ["Position prediction", "Instant results", "Works every time"],
    },
    "Thimbles": {
      title: "Thimbles Predictor",
      image: "/thimbles.png",
      desc: "Know the ball position every time. Advanced tracking algorithm.",
      price: 100,
      tutorial: "https://t.me/BYTRON_CHANNEL/797",
      features: ["Ball tracking", "100% detection", "Simple interface"],
    },
    "Wild West Gold": {
      title: "Wild West Gold Predictor",
      image: "/wild-west-gold.png",
      desc: "Know the gold position and win easily. Advanced slot analysis.",
      price: 100,
      tutorial: "https://t.me/BYTRON_CHANNEL/797",
      features: ["Slot analysis", "Win prediction", "Bonus detection"],
    },
    "Higher vs Lower": {
      title: "Higher vs Lower Predictor",
      image: "/higher-vs-lower.png",
      desc: "Know the hidden number before choosing and win every round.",
      price: 100,
      tutorial: "https://t.me/BYTRON_CHANNEL/797",
      features: ["Number prediction", "Always correct", "Easy to use"],
    },
    "Dragons Gold": {
      title: "Dragons Gold Predictor",
      image: "/dragons-gold.png",
      desc: "Know the gold position and win easily. Dragon-themed slot analysis.",
      price: 100,
      tutorial: "https://t.me/BYTRON_CHANNEL/797",
      features: ["Gold detection", "Pattern analysis", "High win rate"],
    },
  };

  useEffect(() => {
    setProduct(PRODUCTS[productId]);
    window.scrollTo(0, 0);
  }, [productId]);

  if (!product) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-white px-4"
        style={{ background: `linear-gradient(180deg, ${theme.bgFrom}, ${theme.bgTo})` }}
      >
        <div className="text-center">
          <p className="text-lg sm:text-xl mb-4">Product not found</p>
          <Link to="/" className="text-amber-400 hover:text-amber-300 active:text-amber-200">
            ← Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen text-white"
      style={{ background: `linear-gradient(180deg, ${theme.bgFrom}, ${theme.bgTo})` }}
    >
      <div className="max-w-4xl mx-auto p-4 sm:p-6 pt-4 sm:pt-8 pb-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/70 hover:text-white active:text-white/80 mb-4 sm:mb-6 transition py-2"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm sm:text-base">Back to Catalog</span>
        </Link>

        <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
          <div>
            <div
              className="rounded-xl sm:rounded-2xl border-2 sm:border-4 overflow-hidden"
              style={{ borderColor: theme.accent }}
            >
              <img src={product.image} className="w-full aspect-video object-cover" alt={product.title} />
            </div>
            
            <div className="mt-4 grid grid-cols-3 gap-2">
              <div className="flex flex-col items-center p-3 rounded-lg bg-white/5 border border-white/10">
                <Shield className="h-5 w-5 text-green-400 mb-1" />
                <span className="text-xs text-white/60">Safe</span>
              </div>
              <div className="flex flex-col items-center p-3 rounded-lg bg-white/5 border border-white/10">
                <Zap className="h-5 w-5 text-amber-400 mb-1" />
                <span className="text-xs text-white/60">Fast</span>
              </div>
              <div className="flex flex-col items-center p-3 rounded-lg bg-white/5 border border-white/10">
                <Clock className="h-5 w-5 text-blue-400 mb-1" />
                <span className="text-xs text-white/60">24/7</span>
              </div>
            </div>
          </div>

          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">{product.title}</h1>
            
            <div className="flex items-center gap-2 mb-4">
              <div className="flex">
                {[1,2,3,4,5].map(i => (
                  <span key={i} className="text-amber-400">★</span>
                ))}
              </div>
              <span className="text-sm text-white/60">(1,247 reviews)</span>
            </div>

            <div className="mb-4">
              <span className="text-3xl font-bold text-amber-400">${product.price}</span>
              <span className="text-white/60 ml-2 line-through">$199</span>
              <span className="ml-2 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">50% OFF</span>
            </div>

            <p className="text-white/70 mb-6 leading-relaxed text-sm sm:text-base">{product.desc}</p>

            <div className="mb-6">
              <h3 className="font-semibold mb-3">Features:</h3>
              <ul className="space-y-2">
                {product.features?.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-white/70">
                    <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10">
              <h3 className="font-semibold mb-3">How to Use:</h3>
              <ol className="list-decimal pl-5 space-y-1 text-white/60 text-sm">
                <li>Contact us on Telegram to purchase</li>
                <li>Receive download link instantly</li>
                <li>Install and activate with your key</li>
                <li>Open the game and start winning</li>
              </ol>

              {product.tutorial && (
                <a
                  href={product.tutorial}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 active:text-amber-200 text-sm"
                >
                  ▶ Watch Video Tutorial
                </a>
              )}
            </div>

            <button
              onClick={() => window.open(TELEGRAM_CHAT, "_blank")}
              className="w-full py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-2
                bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black transition-all"
            >
              <Download className="h-5 w-5" />
              Buy Now on Telegram
            </button>

            <p className="text-center text-white/50 text-xs sm:text-sm mt-3">
              Instant delivery • Lifetime access • Free updates
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
